import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StoryCard from "@/components/feature/StoryCard";
import StoryTimeline from "@/pages/story/components/StoryTimeline";
import StoryResponses from "@/pages/story/components/StoryResponses";
import StoryToc from "@/pages/story/components/StoryToc";
import ReadingProgress from "@/pages/story/components/ReadingProgress";
import { useSeo } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { stories } from "@/mocks/stories";

export default function StoryDetailPage() {
  const { id } = useParams<{ id: string }>();

  const story = useMemo(() => stories.find((s) => s.id === id), [id]);

  const related = useMemo(() => {
    if (!story) return [];
    const sameCategory = stories.filter((s) => s.id !== story.id && s.categoryId === story.categoryId);
    const others = stories.filter((s) => s.id !== story.id && s.categoryId !== story.categoryId);
    return [...sameCategory, ...others].slice(0, 3);
  }, [story]);

  const tocEntries = useMemo(
    () => (story ? story.sections.map((s, i) => ({ id: `section-${i}`, heading: s.heading })) : []),
    [story],
  );

  const jsonLd = useMemo(() => {
    if (!story) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: story.title,
        description: story.excerpt,
        articleSection: story.category,
        keywords: story.tags.join(","),
        datePublished: story.date,
        dateModified: story.date,
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: "黑暗爆料" },
        publisher: { "@type": "Organization", name: "黑暗爆料" },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://heianbaoliao.example.com/story/${story.id}`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://heianbaoliao.example.com/" },
          { "@type": "ListItem", position: 2, name: "爆料库", item: "https://heianbaoliao.example.com/stories" },
          {
            "@type": "ListItem",
            position: 3,
            name: story.title,
            item: `https://heianbaoliao.example.com/story/${story.id}`,
          },
        ],
      },
    ];
  }, [story]);

  useSeo({
    title: story ? `${story.title} - ${story.category}｜黑暗爆料` : "稿件未找到｜黑暗爆料",
    description: story
      ? story.excerpt
      : "该稿件可能已被更正、合并或移除，请在爆料库中查看其他内容。",
    keywords: story ? story.tags.join(",") : "黑暗爆料,匿名爆料",
    path: `/story/${id}`,
    jsonLd,
  });

  useScrollReveal([id]);

  if (!story) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">找不到这篇稿件</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            它可能已被更正、合并进专题，或暂时下架。你可以回到爆料库，按栏目继续浏览其他内容。
          </p>
          <Link
            to="/stories"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回爆料库
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <ReadingProgress />

      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
            <Breadcrumbs
              items={[
                { name: "爆料库", href: "/stories" },
                { name: story.category, href: `/stories?category=${story.categoryId}` },
                { name: "详情" },
              ]}
            />

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-600 px-2.5 py-1 text-xs font-label font-medium text-background-50">
                {story.category}
              </span>
              {story.verified ? (
                <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-label font-medium text-accent-900">
                  <i className="ri-verified-badge-line" aria-hidden="true"></i>
                  已交叉核实
                </span>
              ) : (
                <span className="flex items-center gap-1 rounded-md bg-background-200 px-2.5 py-1 text-xs font-label text-foreground-600">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  待进一步核实
                </span>
              )}
              {story.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md border border-background-300 px-2.5 py-1 text-xs font-label text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
              {story.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-label text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-voice-line text-base text-primary-600" aria-hidden="true"></i>
                {story.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-base" aria-hidden="true"></i>
                {story.region}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-base" aria-hidden="true"></i>
                {story.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-base" aria-hidden="true"></i>
                阅读约 {story.readMinutes} 分钟
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-line text-base" aria-hidden="true"></i>
                热度 {story.heat}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <figure className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <img
                  src={story.cover}
                  alt={`${story.title} 配图`}
                  title={`${story.title} - ${story.category}`}
                  className="h-64 w-full object-cover object-top md:h-96"
                />
              </figure>

              <p className="mt-7 border-l-4 border-primary-500 bg-background-100 px-5 py-4 text-base leading-relaxed text-foreground-800">
                {story.excerpt}
              </p>

              <div className="mt-9 space-y-8">
                {story.sections.map((section, i) => (
                  <section key={section.heading} id={`section-${i}`} className="scroll-mt-28">
                    <h2 className="flex items-baseline gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      <span className="font-heading text-sm text-primary-500">{String(i + 1).padStart(2, "0")}</span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((p) => (
                        <p key={p.slice(0, 18)} className="text-[15px] leading-8 text-foreground-700">
                          {p}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <StoryResponses items={story.responses} />

              <aside className="mt-10 flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 px-5 py-4">
                <i className="ri-error-warning-line mt-0.5 text-xl text-accent-600" aria-hidden="true"></i>
                <p className="text-xs leading-relaxed text-foreground-600">
                  说明：本篇为信源口述与部分材料线索整理，涉及的个人、机构与地点均已做脱敏处理。稿件不对任何主体下结论，事实以有权机关的结论为准。若你是相关方或知情者，欢迎通过投稿渠道补充、更正或回应。
                </p>
              </aside>

              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
                <span className="text-sm font-label text-foreground-500">觉得这条线索有价值？</span>
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
                >
                  <i className="ri-lock-line" aria-hidden="true"></i>
                  我也要爆料
                </Link>
                <Link
                  to={`/stories?category=${story.categoryId}`}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-stack-line" aria-hidden="true"></i>
                  更多{story.category}
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="space-y-6 lg:sticky lg:top-40">
                <StoryToc entries={tocEntries} />
                <StoryTimeline items={story.timeline} />
                <div className="rounded-lg border border-background-200 bg-secondary-900 p-5 text-background-100">
                  <h2 className="flex items-center gap-2 text-sm font-label font-semibold text-background-50">
                    <i className="ri-shield-user-line text-accent-400" aria-hidden="true"></i>
                    你的身份会被保护
                  </h2>
                  <p className="mt-3 text-xs leading-relaxed text-background-300">
                    投稿不收集可识别信息，线人代号在内部随机生成。若你掌握更多细节，可以从这里安全地告诉我们。
                  </p>
                  <Link
                    to="/submit"
                    className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-xs font-label font-semibold text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
                  >
                    <i className="ri-send-plane-line" aria-hidden="true"></i>
                    匿名投稿
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14 border-t border-background-200 pt-10">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-links-line text-primary-600" aria-hidden="true"></i>
              相关爆料推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((item) => (
                <StoryCard key={item.id} story={item} />
              ))}
            </div>
          </section>
        </div>
      </article>
    </SiteLayout>
  );
}